import React, { useMemo } from 'react';
import classNames from 'classnames';

import { flattenTree } from './flattenTree';
import TreeTableBodyRow from './TreeTableBodyRow';
import TreeTableHeader from './TreeTableHeader';
import { TreeTableProps } from './types';

/**
 * An expandable hierarchical table driven by a column config. Expansion is controlled through `isExpanded` and
 * `onToggle`; `useTreeExpansion` is the default way to get them. Collapsed rows are not rendered.
 *
 * Declared as a generic function: React.FC cannot carry a type parameter.
 */
const TreeTable = <T,>({
  rows,
  treeColumn,
  columns,
  isExpanded,
  onToggle,
  toggleLabel,
  caption,
  minWidth = 0,
  className,
  rowClassName,
  dataCy = 'tree-table',
}: TreeTableProps<T>): React.ReactElement => {
  const flatRows = useMemo(() => flattenTree(rows, isExpanded), [rows, isExpanded]);

  return (
    <div className="tree-table-scroll">
      <table
        className={classNames('tree-table', className)}
        style={{ '--tree-table-min-width': `${minWidth}px` } as React.CSSProperties}
        data-cy={dataCy}
      >
        <caption className="visually-hidden">{caption}</caption>
        <TreeTableHeader treeColumn={treeColumn} columns={columns} />
        <tbody>
          {flatRows.map((node) => (
            <TreeTableBodyRow
              key={node.row.id}
              node={node}
              treeColumn={treeColumn}
              columns={columns}
              onToggle={onToggle}
              toggleLabel={toggleLabel}
              className={rowClassName?.(node.row.data, node.depth)}
              dataCy={dataCy}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TreeTable;
